import { CompetencyBadge } from '@/components/feedback/CompetencyBadge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { scoreColor } from '@/lib/scoreColors'
import { cn } from '@/lib/utils'
import type { StoryDetail, StoryPatch } from '@/types/interview'
import { Loader2, Lock, Star } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

// A first click arms "Delete story"; a second click within this window confirms
// permanent deletion. Matches the confirm pattern on StoryCard.
const DELETE_CONFIRM_TIMEOUT_MS = 3000

const STAR_SECTIONS = [
  { key: 'situation', label: 'Situation' },
  { key: 'task', label: 'Task' },
  { key: 'action', label: 'Action' },
  { key: 'result', label: 'Result' },
] as const

interface StoryDetailSheetProps {
  storyId: string | null
  isPro: boolean
  loadStory: (storyId: string) => Promise<StoryDetail | null>
  onPatch: (storyId: string, patch: StoryPatch) => Promise<boolean>
  onDelete: (storyId: string) => void
  onClose: () => void
  onViewed: (story: StoryDetail) => void
}

export function StoryDetailSheet({
  storyId,
  isPro,
  loadStory,
  onPatch,
  onDelete,
  onClose,
  onViewed,
}: StoryDetailSheetProps) {
  const [story, setStory] = useState<StoryDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftNotes, setDraftNotes] = useState('')
  const [draftStar, setDraftStar] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    if (!confirmingDelete) return
    const timer = setTimeout(() => setConfirmingDelete(false), DELETE_CONFIRM_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [confirmingDelete])

  // Keep the latest callbacks in refs so the load effect below depends only on
  // storyId — a parent re-render that produces new (but behaviorally identical)
  // loadStory/onViewed references must not re-trigger a reload and wipe
  // in-progress edit state (e.g. clicking Favorite while editing).
  const loadStoryRef = useRef(loadStory)
  const onViewedRef = useRef(onViewed)
  useEffect(() => {
    loadStoryRef.current = loadStory
    onViewedRef.current = onViewed
  })

  useEffect(() => {
    // A different story (or closing the sheet) must never inherit an armed delete
    setConfirmingDelete(false)
    if (!storyId) {
      setStory(null)
      setEditing(false)
      return
    }
    let cancelled = false
    setLoading(true)
    void loadStoryRef.current(storyId).then((s) => {
      if (cancelled) return
      setStory(s)
      setLoading(false)
      if (s) {
        setDraftTitle(s.title)
        setDraftNotes(s.notes ?? '')
        setDraftStar({
          situation: s.star?.situation ?? '',
          task: s.star?.task ?? '',
          action: s.star?.action ?? '',
          result: s.star?.result ?? '',
        })
        onViewedRef.current(s)
      }
    })
    return () => {
      cancelled = true
    }
    // storyId only — loadStory/onViewed are read via the refs above so they
    // don't need to be (and shouldn't be) tracked dependencies here.
  }, [storyId])

  // Re-seed drafts from the saved story every time editing starts, not only on
  // initial load — otherwise Cancel-then-Edit-again shows the abandoned draft
  // instead of the last saved values.
  function startEditing() {
    if (!story) return
    setDraftTitle(story.title)
    setDraftNotes(story.notes ?? '')
    setDraftStar({
      situation: story.star?.situation ?? '',
      task: story.star?.task ?? '',
      action: story.star?.action ?? '',
      result: story.star?.result ?? '',
    })
    setEditing(true)
  }

  async function handleSave() {
    if (!story) return
    setSaving(true)
    setSaveError(false)

    const patch: StoryPatch = {}
    if (draftTitle.trim() && draftTitle.trim() !== story.title) patch.title = draftTitle.trim()
    if (draftNotes.trim() !== (story.notes ?? '')) patch.notes = draftNotes.trim() || null
    if (story.source !== 'session_answer' && story.star) {
      const starPatch: NonNullable<StoryPatch['star']> = {}
      if (
        draftStar.situation?.trim() &&
        draftStar.situation.trim() !== (story.star.situation ?? '')
      ) {
        starPatch.situation = draftStar.situation.trim()
      }
      for (const key of ['task', 'action', 'result'] as const) {
        if ((draftStar[key] ?? '').trim() !== (story.star[key] ?? '')) {
          starPatch[key] = (draftStar[key] ?? '').trim() || null
        }
      }
      if (Object.keys(starPatch).length > 0) patch.star = starPatch
    }

    if (Object.keys(patch).length === 0) {
      setEditing(false)
      setSaving(false)
      return
    }

    const ok = await onPatch(story.id, patch)
    setSaving(false)
    if (ok) {
      setStory((prev) =>
        prev
          ? {
              ...prev,
              ...(patch.title && { title: patch.title }),
              ...(patch.notes !== undefined && { notes: patch.notes }),
              ...(patch.star && prev.star ? { star: { ...prev.star, ...patch.star } } : {}),
            }
          : prev
      )
      setEditing(false)
    } else {
      setSaveError(true)
    }
  }

  async function handleFavorite() {
    if (!story) return
    const next = !story.isFavorite
    setStory((prev) => (prev ? { ...prev, isFavorite: next } : prev))
    const ok = await onPatch(story.id, { isFavorite: next })
    if (!ok) setStory((prev) => (prev ? { ...prev, isFavorite: !next } : prev))
  }

  return (
    <Dialog open={storyId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && !story && storyId && (
          <p className="text-sm text-muted-foreground py-8 text-center">
            We could not load this story. Please try again.
          </p>
        )}

        {!loading && story && (
          <>
            <DialogHeader className="text-left space-y-2">
              <div className="flex items-start justify-between gap-2 pr-6">
                {editing ? (
                  <input
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    maxLength={120}
                    className="flex-1 rounded-lg border border-border bg-background px-2 py-1 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label="Story title"
                  />
                ) : (
                  <DialogTitle className="text-base leading-snug">{story.title}</DialogTitle>
                )}
                {isPro && (
                  <button
                    type="button"
                    onClick={() => void handleFavorite()}
                    aria-label={
                      story.isFavorite ? 'Remove from best answers' : 'Mark as best answer'
                    }
                    className="flex-shrink-0 rounded-md p-1 hover:bg-muted transition-colors"
                  >
                    <Star
                      className={cn(
                        'h-4 w-4',
                        story.isFavorite ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'
                      )}
                    />
                  </button>
                )}
              </div>
              <DialogDescription className="flex items-center gap-2 flex-wrap">
                {story.competency && <CompetencyBadge competency={story.competency} />}
                {story.targetRole && <span className="text-xs">{story.targetRole}</span>}
                {story.roleTag && <span className="text-xs">{story.roleTag}</span>}
                {story.scores && (
                  <span className={cn('text-xs font-semibold', scoreColor(story.scores.overall))}>
                    {story.scores.overall.toFixed(1)} overall
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            {/* Body: answer text (session) or STAR blocks (standalone) */}
            {story.storyText != null ? (
              <div className="space-y-2">
                {story.questionText && (
                  <p className="text-xs text-muted-foreground italic">{story.questionText}</p>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{story.storyText}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {STAR_SECTIONS.map(({ key, label }) => {
                  const value = story.star?.[key]
                  if (!editing && !value) return null
                  return (
                    <div key={key} className="space-y-1">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        {label}
                      </p>
                      {editing ? (
                        <textarea
                          value={draftStar[key] ?? ''}
                          onChange={(e) =>
                            setDraftStar((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                          rows={2}
                          maxLength={2000}
                          className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                          aria-label={`${label} text`}
                        />
                      ) : (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{value}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Per-component feedback (session answers) */}
            {story.feedback && (
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Coach feedback
                </p>
                {STAR_SECTIONS.map(({ key, label }) => {
                  const text = story.feedback?.[key]
                  if (!text) return null
                  return (
                    <p key={key} className="text-xs leading-relaxed">
                      <span className="font-semibold">{label}:</span>{' '}
                      <span className="text-muted-foreground">{text}</span>
                    </p>
                  )
                })}
              </div>
            )}

            {/* Rewrite example */}
            {story.rewrite && (
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Example answer
                </p>
                <p className="text-xs leading-relaxed whitespace-pre-wrap">
                  {story.rewrite.exampleAnswer}
                </p>
                <p className="text-xs text-muted-foreground italic">
                  {story.rewrite.whatMakesItStrong}
                </p>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-1">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Your notes
              </p>
              {editing ? (
                <textarea
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Cues to remember before an interview — the metric to lead with, what to trim…"
                  className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                  aria-label="Story notes"
                />
              ) : story.notes ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{story.notes}</p>
              ) : (
                <p className="text-xs text-muted-foreground italic">No notes yet.</p>
              )}
            </div>

            {saveError && (
              <p role="alert" className="text-xs text-destructive">
                We could not save your changes. Please try again.
              </p>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => {
                  if (!confirmingDelete) {
                    setConfirmingDelete(true)
                    return
                  }
                  onDelete(story.id)
                  onClose()
                }}
                onBlur={() => setConfirmingDelete(false)}
                className={cn(
                  'text-xs transition-colors',
                  confirmingDelete
                    ? 'font-semibold text-destructive'
                    : 'text-muted-foreground hover:text-destructive'
                )}
              >
                {confirmingDelete ? 'Click again to permanently delete' : 'Delete story'}
              </button>
              {isPro ? (
                editing ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      disabled={saving}
                      className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-40"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleSave()}
                      disabled={saving}
                      className="rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-40"
                    >
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={startEditing}
                    className="rounded-lg border border-border px-4 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
                  >
                    Edit
                  </button>
                )
              ) : (
                <Link
                  to="/settings"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                >
                  <Lock className="h-3 w-3" />
                  Editing is a Pro feature — upgrade
                </Link>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
